import request from "../libs/request";

import { BuildingId } from "../types/building";
import {
  MemberInfo,
  NexpaInfo,
  NickNameRequest,
  PushItem,
} from "../types/member";

import { FindIdRequest } from "./authServices";
import { PublicTermPurpose } from "./etcServices";
import { PageableRequest, PageableResponse } from "./talkServices";

const baseUrl = "member";

export const getPushList = (params: Partial<PageableRequest>) =>
  request.get<PageableResponse<PushItem>>(baseUrl + "/push/list", { params });

export const updatePushRead = (pushId: number) =>
  request.patch(baseUrl + `/push/${pushId}/read`);

export const getNexpaInfo = () =>
  request.get<NexpaInfo>(baseUrl + "/nexpa-info");

export type ProfileImage = {
  fileId: string;
  filePath: string;
};

export type AppSetting = {
  simpleLogin: boolean;
  maintainLogin: boolean;
};

export type UserAlarm = {
  advertise: boolean;
  incommodity: boolean;
  rent: boolean;
  reservation: boolean;
  service: boolean;
  talkReply: boolean;
};

export type ManagerAlarm = {
  dontSturb: boolean;
  dontSturbEnd: boolean;
  dontSturbStart: boolean;
  incommodityCleaning: boolean;
  incommodityCommon: boolean;
  incommodityEtc: boolean;
  incommodityFacility: boolean;
  monitoring: boolean;
  rent: boolean;
  reservation: boolean;
  restaurant: boolean;
  service: boolean;
};

export const MANAGER_ROLE = {
  FIELD: "ROLE_FIELD",
  MONITORING: "ROLE_MONITORING",
  MANAGER: "ROLE_MANAGER",
  CALL_CENTER: "ROLE_CALL_CENTER",
  CAFETERIA: "ROLE_CAFETERIA",
  USER: "ROLE_USER",
  ADMIN: "ROLE_ADMIN",
  SPACE: "ROLE_SPACE",
  FREE_LOCATION: "ROLE_FREE_LOCATION",
};

export type BuildingInfo = BuildingId & {
  buildingName: string;
  buildingFloorId: string;
  buildingFloorName: string;
  buildingSpaceId: string;
  buildingSpaceName: string;
};

export type TermAgreed = {
  termsId: number;
  purpose: PublicTermPurpose;
};

// export type MemberInfo = BuildingInfo & {
//   memberId: number;
//   createdAt: string;
//   name: string;
//   email: string;
//   status: string;
//   nickname: string;
//   isSelfCertificated: boolean;
//   isCompanyCertificated: boolean;
//   isJoinWithMyLgId: boolean;
//   gender: string;
//   mobileNumber: string | null;
//   role: string;
//   roles: string[];
//   companyId: string;
//   companyName: string;
//   lastLoggedAt?: string | null;
//   deviceType: string | null;
//   profileImage: ProfileImage;
//   appSettings: AppSetting | null;
//   managerAlarm: ManagerAlarm;
//   alarm: UserAlarm;
//   isEssentialTermsAgreed: boolean | null;
//   memberAgreedTermsList: TermAgreed[];
// };

export const getMemberInfo = () => request.get<MemberInfo>(baseUrl + "/info");

export const updateNickname = (params: NickNameRequest) =>
  request.patch(baseUrl + "/nickname", params);

export type SpaceItem = BuildingId & {
  memberId: number;
  companyId: string | null;
  companyName: string | null;
  buildingName: string;
  buildingFloorId: string;
  buildingFloorName: string;
  buildingSpaceId: string;
  buildingSpaceName: string;
  isMain: string | null;
};

export const getMemberSpaceList = () =>
  request.get<SpaceItem[]>(baseUrl + "/space");

export type UpdateSpaceRequest = BuildingId & {
  buildingFloorId: string;
  buildingSpaceId: string;
};

export const updateMemberSpace = (params: UpdateSpaceRequest) =>
  request.patch(baseUrl + "/space", params);

export type UpdateCompanyRequest = {
  memberId: number;
  companyId: string;
};

export const updateMemberCompany = (params: UpdateCompanyRequest) =>
  request.patch(baseUrl + "/company", params);

export type PasswordRequest = {
  password: string;
};

export const updateMemberPassword = (params: PasswordRequest) =>
  request.patch(baseUrl + "/password", params);

export const updateMemberProfile = (params: FormData) =>
  request.patch(baseUrl + "/profile-image", params, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const withdrawMember = () => request.post(baseUrl + "/withdraw");

export const getMemberCompanyVerification = (params: {
  authCode: string;
  email: string;
}) => request.get(baseUrl + "/company/verification", { params });

export type CompanyVerification = {
  email: string;
};

export const postMemberCompanyVerification = (params: CompanyVerification) =>
  request.post(baseUrl + "/company/verification", params);

export type ChangeMemberCertificationRequest = FindIdRequest & {
  memberId: number;
};

export const changeMemberCertification = (
  params: ChangeMemberCertificationRequest
) => request.patch(baseUrl + "/changeMemberCert", params);

export const reactivateMember = () => request.post(baseUrl + "/reactivation");

export type CompanyDomainResponse = {
  companyId: string;
  domains: string[];
};

export const getCompanyDomain = () => request.get(baseUrl + "/company/domain");

export type AppVersionRequest = {
  appVersion: string;
  deviceType: string;
};

export const updateAppVersion = (params: AppVersionRequest) =>
  request.patch(baseUrl + "/app-version", params);

export const sendChangeEmail = (params: { email: string }) =>
  request.post(baseUrl + "/email/send", params);

export const getSandiMarketUrl = () => request.get("/lifecare");

export const getEssentialTerms = () => request.get(baseUrl + "/essentialTerms");
